import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import { NavLink } from '@/components/NavLink';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';
import { useOnboardingState } from '@/hooks/useOnboardingState';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { useSorvaineAdmin } from '@/hooks/useSorvaineAdmin';
import { useSubscription } from '@/hooks/useSubscription';
import { useOrganization } from '@/lib/organization-context';
import { isTeamOrHigher, TEAM_ONLY_ANALYTICS_VIEWS } from '@/lib/plans';
import { NotificationBell } from '@/components/notifications';
import { AnnouncementBell } from '@/components/admin/AnnouncementBanner';
import { useProjectFilters } from '@/contexts/ProjectFilterContext';
import { defaultFilters } from '@/components/projects/ProjectFilters';
import { HelpSupportDialog } from './HelpSupportDialog';
import { HelpDrawer } from '@/components/help/HelpDrawer';

import logoSrc from '@/assets/sorvaine-icon.png';
import { successToast, errorToast, infoToast } from '@/components/ui/sonner';
import {
  Lock,
  Zap,
  LayoutDashboard,
  HelpCircle,
  FolderKanban,
  Library,
  BarChart3,
  Sparkles,
  Settings,
  LogOut,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Play,
  Trash2,
  TrendingUp,
  Clock,
  FileText,
  Building2,
  MessageSquare,
  Users,
  Palette,
  Plug,
  Shield,
  FolderOpen,
  Archive,
  Plus,
  CheckCircle2,
  Star,
  History,
  Upload,
  Trophy,
  Crosshair,
  Lightbulb,
  Import,
  Sun,
  Moon,
  ImageIcon,
  Briefcase,
  Scale,
  BookOpen,
  Server,
  Globe,
  Bug,
  LifeBuoy,
  MessageCircle,
  Bell,
  ScrollText,
} from 'lucide-react';
import { openCrispChat } from '@/lib/crisp';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SearchInput } from '@/components/ui/search-input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Navigation items for the icon rail
const mainNavItems = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', url: '/dashboard' },
  { id: 'projects', icon: FolderKanban, label: 'Proposals', url: '/projects' },
  { id: 'library', icon: Library, label: 'Knowledge Vault', url: '/library' },
  { id: 'analytics', icon: BarChart3, label: 'Analytics', url: '/analytics' },
  
];

// Contextual sub-navigation for each section
interface SubNavItem {
  icon: React.ElementType;
  label: string;
  url?: string;
  action?: string;
  tooltip?: string;
  children?: { icon?: React.ElementType; label: string; url?: string }[];
}

interface SubNavSection {
  title: string;
  items: SubNavItem[];
}

const getSubNavContent = (sectionId: string): { title: string; sections: SubNavSection[] } => {
  const contentMap: Record<string, { title: string; sections: SubNavSection[] }> = {
    dashboard: {
      title: 'Dashboard',
      sections: [],
    },
    projects: {
      title: 'Proposals',
      sections: [],
    },
    library: {
      title: 'Knowledge Vault',
      sections: [
        {
          title: 'Views',
          items: [
            { icon: FileText, label: 'Content', url: '/library' },
            { icon: ImageIcon, label: 'Media', url: '/library?view=media' },
            { icon: Archive, label: 'RFP Archive', url: '/library?view=rfp-archive' },
          ],
        },
      ],
    },
    analytics: {
      title: 'Analytics',
      sections: [
        {
          title: 'Views',
          items: [
            { icon: LayoutDashboard, label: 'Overview', url: '/analytics' },
            { icon: TrendingUp, label: 'Bid Intelligence', url: '/analytics?view=bid-intelligence' },
            { icon: Trophy, label: 'Vault Performance', url: '/analytics?view=vault-performance' },
            
            { icon: Crosshair, label: 'Competitive', url: '/analytics?view=competitive' },
            { icon: Lightbulb, label: 'Recommendations', url: '/analytics?view=recommendations' },
          ],
        },
      ],
    },
    settings: {
      title: 'Settings',
      sections: [
        {
          title: 'Personal',
          items: [
            { icon: Users, label: 'Profile', url: '/settings?tab=profile' },
            { icon: Palette, label: 'Preferences', url: '/settings?tab=preferences' },
            { icon: Bell, label: 'Notifications', url: '/settings?tab=notifications' },
            { icon: Shield, label: 'Security', url: '/settings?tab=security' },
            { icon: Trash2, label: 'Account', url: '/settings?tab=account' },
          ],
        },
        {
          title: 'Workspace',
          items: [
            { icon: Building2, label: 'General', url: '/settings?tab=company' },
            { icon: Plug, label: 'Members', url: '/settings?tab=organization' },
            { icon: ScrollText, label: 'Audit log', url: '/settings?tab=audit' },
            { icon: Library, label: 'Library teams', url: '/settings?tab=library' },
            { icon: FileText, label: 'Billing', url: '/settings?tab=billing' },
          ],
        },
      ],
    },
  };

  return contentMap[sectionId] || contentMap.dashboard;
};

// Determine active section from current path
const getActiveSectionFromPath = (pathname: string): string => {
  if (pathname.startsWith('/projects')) return 'projects';
  if (pathname.startsWith('/library')) return 'library';
  if (pathname.startsWith('/analytics')) return 'analytics';
  
  
  if (pathname.startsWith('/settings') || pathname.startsWith('/admin')) return 'settings';
  return 'dashboard';
};

// Icon Rail Component
function IconRail({
  activeSection,
  onSectionChange,
  isDetailCollapsed,
  onExpandDetail,
}: {
  activeSection: string;
  onSectionChange: (section: string) => void;
  isDetailCollapsed: boolean;
  onExpandDetail: () => void;
}) {
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const { replayTutorial, developerReset } = useOnboardingState();
  const { theme, toggleTheme } = useTheme();
  const { isAdmin } = useAdminCheck();
  const { isSorvaineAdmin } = useSorvaineAdmin();
  const { organization } = useOrganization();
  
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);

  const handleOpenSupport = async () => {
    const ok = await openCrispChat({
      email: user?.email,
      nickname: user?.user_metadata?.name,
      company: organization?.name,
    });
    if (!ok) {
      // Crisp disabled — fall back to the existing Help & Support modal
      setHelpOpen(true);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleReplayTutorial = async () => {
    try {
      await replayTutorial();
      infoToast('Tutorial ready', 'Loading the onboarding experience...');
    } catch (error) {
      errorToast('Could not replay tutorial', 'Please try again.');
    }
  };

  const handleDeveloperReset = async () => {
    setIsResetting(true);
    try {
      await developerReset();
      successToast('Reset complete', 'All data has been wiped. Redirecting to onboarding...');
      setShowResetDialog(false);
      navigate('/dashboard');
      window.location.reload();
    } catch (error) {
      console.error('Developer reset failed:', error);
      errorToast('Reset failed', 'There was an error resetting your data. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <TooltipProvider delayDuration={100}>
      <div id="app-two-level-sidebar" className="w-12 h-full flex flex-col bg-background border-r border-border/50">
        {/* Logo */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={isDetailCollapsed ? onExpandDetail : undefined}
              className={cn(
                "h-14 w-full flex items-center justify-center border-b border-border/50",
                isDetailCollapsed && "cursor-pointer hover:bg-muted/30 transition-colors"
              )}
            >
              <div className="rounded-md overflow-hidden flex items-center justify-center">
                <img src={logoSrc} alt="Sorvaine" className="w-55 h-55 object-contain" />
              </div>
            </button>
          </TooltipTrigger>
          {isDetailCollapsed && (
            <TooltipContent side="right" sideOffset={8}>
              Expand navigation
            </TooltipContent>
          )}
        </Tooltip>

        {/* Main Navigation */}
        <div className="flex-1 py-4 flex flex-col items-center gap-0.5">
          {mainNavItems.map((item) => (
            <Tooltip key={item.id}>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    onSectionChange(item.id);
                    navigate(item.url);
                  }}
                  className={cn(
                    "w-9 h-9 rounded-lg flex items-center justify-center transition-colors",
                    activeSection === item.id
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  <item.icon className="w-[18px] h-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={8}>
                {item.label}
              </TooltipContent>
            </Tooltip>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="py-4 flex flex-col items-center gap-0.5 border-t border-border/50">
          {/* Announcements */}
          <div className="w-9 h-9 flex items-center justify-center">
            <AnnouncementBell />
          </div>
          {/* Notifications */}
          <div className="w-9 h-9 flex items-center justify-center">
            <NotificationBell />
          </div>

          {/* Settings */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => {
                  onSectionChange('settings');
                  navigate('/settings');
                }}
                className={cn(
                  "w-9 h-9 rounded-lg flex items-center justify-center transition-colors",
                  activeSection === 'settings'
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                <Settings className="w-[18px] h-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              Settings
            </TooltipContent>
          </Tooltip>

          {/* Help & Support */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setHelpOpen(true)}
                className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/50"
              >
                <HelpCircle className="w-[18px] h-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              Help & Support
            </TooltipContent>
          </Tooltip>

          {/* Live Support Chat (Crisp) */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleOpenSupport}
                className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors text-muted-foreground hover:text-foreground hover:bg-muted/50"
                aria-label="Support chat"
              >
                <MessageCircle className="w-[18px] h-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              Support
            </TooltipContent>
          </Tooltip>

          {/* User Avatar Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger className="focus:outline-none">
              <Avatar className="h-8 w-8 cursor-pointer hover:ring-2 hover:ring-primary/20 transition-all">
                <AvatarFallback className="text-xs font-medium bg-primary text-primary-foreground">
                  {user?.email?.[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" sideOffset={8} className="w-56">
              <div className="px-3 py-2">
                <p className="text-sm font-medium">{user?.user_metadata?.name || 'User'}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              {isSorvaineAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer">
                    <Server className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleReplayTutorial} className="cursor-pointer">
                <Play className="w-4 h-4 mr-2" />
                Replay Onboarding
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setReportOpen(true)} className="cursor-pointer">
                <Bug className="w-4 h-4 mr-2" />
                Report a problem
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setHelpDrawerOpen(true)} className="cursor-pointer">
                <LifeBuoy className="w-4 h-4 mr-2" />
                Help Center
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem 
                  onClick={() => setShowResetDialog(true)} 
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Developer Reset
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <div
                className="flex items-center justify-between px-4 py-2.5 text-[13px] text-muted-foreground cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleTheme(); }}
                role="menuitem"
              >
                <span>Appearance</span>
                <div className="flex items-center">
                  <Sun className="w-4 h-4 text-muted-foreground theme-icon-sun" />
                  <Moon className="w-4 h-4 text-muted-foreground theme-icon-moon" />
                </div>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Help & Support Modal */}
        <HelpSupportDialog open={helpOpen} onOpenChange={setHelpOpen} />
        <HelpSupportDialog
          open={reportOpen}
          onOpenChange={setReportOpen}
          initialCategory="Bug report"
          title="Report a problem"
        />

        {/* In-app Help Center drawer */}
        <HelpDrawer open={helpDrawerOpen} onOpenChange={setHelpDrawerOpen} />

        {/* Developer Reset Dialog */}
        <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Developer Reset</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>
                  This will <strong>permanently delete</strong> all your data:
                </p>
                <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                  <li>All proposals and data</li>
                  <li>All library content (Q&A, case studies, templates)</li>
                  <li>All analytics and simulation results</li>
                  <li>All notifications and comments</li>
                </ul>
                <p className="font-medium mt-3">
                  This action cannot be undone.
                </p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isResetting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeveloperReset}
                disabled={isResetting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isResetting ? 'Deleting...' : 'Delete Everything'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}

// Detail Panel Component
function DetailPanel({
  activeSection,
  isCollapsed,
  onToggleCollapse,
}: {
  activeSection: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();
  const rawContent = getSubNavContent(activeSection);
  const { planType } = useSubscription();
  const isTeamPlan = isTeamOrHigher(planType);
  const {
    canManageBilling,
    canEditWorkspace,
    canViewTeam,
    canViewAnalytics,
  } = useOrganization();

  // RBAC: hide nav items the current role cannot access.
  // Billing → owner only. Workspace settings (General, Audit, Library teams) → admin+.
  // Members list → contributor+. Analytics section → contributor+.
  const isHiddenByRole = (url?: string): boolean => {
    if (!url) return false;
    if (url.includes('tab=billing')) return !canManageBilling;
    if (url.includes('tab=company') || url.includes('tab=audit') || url.includes('tab=library')) return !canEditWorkspace;
    if (url.includes('tab=organization')) return !canViewTeam;
    if (url.startsWith('/analytics')) return !canViewAnalytics;
    return false;
  };

  // Apply search filtering + role filtering
  const content = {
    ...rawContent,
    sections: rawContent.sections.map(section => ({
      ...section,
      items: section.items
        .filter(item => !isHiddenByRole(item.url))
        .filter(item =>
          searchValue.trim()
            ? item.label.toLowerCase().includes(searchValue.trim().toLowerCase())
            : true
        ),
    })).filter(section => section.items.length > 0),
  };

  // Get filter context for project filtering actions
  const { setFilters, clearFilters } = useProjectFilters();

  const toggleExpanded = (itemKey: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemKey)) next.delete(itemKey);
      else next.add(itemKey);
      return next;
    });
  };

  // Handle filter actions for project sidebar items
  const handleFilterAction = (action: string, url?: string) => {
    switch (action) {
      case 'clear_filters':
        clearFilters();
        break;
      case 'filter_active':
        // Active projects
        setFilters({ ...defaultFilters, statusGroup: 'active' });
        break;
      case 'filter_due_this_week':
        setFilters({ ...defaultFilters, dueDate: 'this_week' });
        break;
      case 'filter_completed':
        // Completed = won, lost, no_decision, no_response, withdrawn
        setFilters({ ...defaultFilters, statusGroup: 'completed' });
        break;
      case 'filter_archived':
        // Archived = withdrawn
        setFilters({ ...defaultFilters, status: 'withdrawn' });
        break;
      default:
        break;
    }
    if (url) {
      navigate(url);
    }
  };

  return (
    <div
      className={cn(
        "h-full bg-background border-r border-border/50 flex flex-col transition-all duration-200 ease-out overflow-hidden",
        isCollapsed ? "w-0" : "w-60"
      )}
    >
      {/* Header with title and collapse button */}
      <div className="h-14 px-4 flex items-center justify-between border-b border-border/50 shrink-0">
        <span className="text-base font-semibold text-foreground truncate">
          {content.title}
        </span>
        <button
          onClick={onToggleCollapse}
          className="w-6 h-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      </div>

      {/* Search */}
      <div className="p-3 shrink-0">
        <SearchInput
          placeholder="Search..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="h-8 text-sm bg-muted/50"
        />
      </div>

      {/* Navigation Sections */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-4">
          {content.sections.length === 0 && (
            <p className="px-4 py-3 text-xs text-muted-foreground">No results</p>
          )}
          {content.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className={sectionIndex > 0 ? 'mt-4' : ''}>
              <div className="px-3 py-2">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.07em]">
                  {section.title}
                </span>
              </div>
              <div className="space-y-0.5">
                {section.items.map((item, itemIndex) => {
                  const itemKey = `${sectionIndex}-${itemIndex}`;
                  const isExpanded = expandedItems.has(itemKey);
                  const hasChildren = item.children && item.children.length > 0;

                  return (
                    <div key={itemIndex}>
                      {item.url && !hasChildren ? (
                        item.action ? (
                          // Item with action - use button that triggers filter + navigation
                          <button
                            onClick={() => handleFilterAction(item.action!, item.url)}
                            className="w-full flex items-center gap-2 px-4 h-10 rounded-md text-sm font-medium text-foreground/85 hover:text-foreground hover:bg-secondary transition-colors"
                          >
                            <item.icon className="w-4 h-4 shrink-0" />
                            <span className="truncate">{item.label}</span>
                          </button>
                        ) : (
                          // Regular NavLink without action
                          (() => {
                            const itemUrl = new URL(item.url!, window.location.origin);
                            const hasQuery = itemUrl.search.length > 0;
                            // For items with query params, match path+search exactly
                            // For items without query params (e.g. /analytics), only active when current URL also has no query params
                            const isExactActive = hasQuery
                              ? location.pathname === itemUrl.pathname && location.search === itemUrl.search
                              : location.pathname === itemUrl.pathname && location.search === '';
                            
                            // Check if this is a Team-only analytics view
                            const viewParam = itemUrl.searchParams.get('view');
                            const isTeamOnly = viewParam && (TEAM_ONLY_ANALYTICS_VIEWS as readonly string[]).includes(viewParam);
                            const isLocked = isTeamOnly && !isTeamPlan;
                            
                            return (
                              <Link
                                to={item.url!}
                                className={cn(
                                  "flex items-center gap-2 px-4 h-10 rounded-md text-sm font-medium text-foreground/85 hover:text-foreground hover:bg-secondary transition-colors",
                                  isExactActive && "bg-primary/10 !text-primary font-semibold",
                                  isLocked && "opacity-60"
                                )}
                              >
                                <item.icon className={cn("w-4 h-4 shrink-0", isExactActive && "text-primary")} />
                                <span className="truncate flex-1">{item.label}</span>
                                {item.tooltip && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <HelpCircle className="w-3.5 h-3.5 shrink-0 text-muted-foreground/50 hover:text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[200px] text-xs">
                                      {item.tooltip}
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {isLocked && <Lock className="w-3 h-3 shrink-0 text-muted-foreground" />}
                              </Link>
                            );
                          })()
                        )
                      ) : (
                        <button
                          onClick={() => hasChildren && toggleExpanded(itemKey)}
                          className="w-full flex items-center gap-2 px-4 h-10 rounded-md text-sm font-medium text-foreground/85 hover:text-foreground hover:bg-secondary transition-colors"
                        >
                          <item.icon className="w-4 h-4 shrink-0" />
                          <span className="truncate flex-1 text-left">{item.label}</span>
                          {hasChildren && (
                            <ChevronDown
                              className={cn(
                                "w-3 h-3 shrink-0 transition-transform duration-200",
                                isExpanded && "rotate-180"
                              )}
                            />
                          )}
                        </button>
                      )}

                      {/* Children */}
                      {hasChildren && isExpanded && (
                        <div className="ml-6 mt-0.5 space-y-0.5">
                          {item.children!.map((child, childIndex) => (
                            <div
                              key={childIndex}
                              className="flex items-center gap-2 px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors cursor-pointer"
                            >
                              {child.icon && <child.icon className="w-3 h-3 shrink-0" />}
                              <span className="truncate">{child.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

    </div>
  );
}

// Collapse Toggle Button (shown when panel is collapsed)
function CollapseToggle({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="h-14 w-5 flex items-center justify-center bg-background border-b border-r border-border/50 text-muted-foreground/40 hover:text-foreground hover:bg-muted/30 transition-colors absolute left-12 top-0 z-10"
    >
      <ChevronRight className="w-3.5 h-3.5" />
    </button>
  );
}

// Helper to detect if we're on a project detail page (not /projects or /projects/new)
const isProjectDetailPage = (pathname: string): boolean => {
  const match = pathname.match(/^\/projects\/([^/]+)/);
  if (!match) return false;
  const projectId = match[1];
  // Exclude /projects/new
  return projectId !== 'new';
};

// Main TwoLevelSidebar Component
export function TwoLevelSidebar() {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(() => 
    getActiveSectionFromPath(location.pathname)
  );
  const [isDetailCollapsed, setIsDetailCollapsed] = useState(false);

  // Update active section when route changes
  const currentSection = getActiveSectionFromPath(location.pathname);
  if (currentSection !== activeSection) {
    setActiveSection(currentSection);
  }

  // Auto-collapse detail panel when entering a project detail page
  useEffect(() => {
    if (isProjectDetailPage(location.pathname)) {
      setIsDetailCollapsed(true);
    }
  }, [location.pathname]);

  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    // Expand the detail panel when changing sections
    if (isDetailCollapsed) {
      setIsDetailCollapsed(false);
    }
  };

  // Hide detail panel entirely when section has no sub-nav content (e.g. Dashboard)
  const hasSubNav = getSubNavContent(activeSection).sections.length > 0;

  return (
    <div className="h-screen flex sticky top-0 z-40 relative">
      <IconRail 
        activeSection={activeSection} 
        onSectionChange={handleSectionChange}
        isDetailCollapsed={isDetailCollapsed || !hasSubNav}
        onExpandDetail={() => hasSubNav && setIsDetailCollapsed(false)}
      />
      {hasSubNav && (
        <DetailPanel
          activeSection={activeSection}
          isCollapsed={isDetailCollapsed}
          onToggleCollapse={() => setIsDetailCollapsed(!isDetailCollapsed)}
        />
      )}
    </div>
  );
}

export default TwoLevelSidebar;
